import { AlertDialog } from "@base-ui/react/alert-dialog";
import { Dialog as Base } from "@base-ui/react/dialog";
import type { ReactNode } from "react";

/**
 * A modal card on Base UI's Dialog: focus moves in and stays in, Escape and a click outside close it,
 * focus goes back to what opened it. Render it while it is open (`{editing && <Dialog … />}`) and close it
 * in `onClose`. `onSubmit` makes the card a form (Enter submits). `alert` is for something that needs an
 * answer, such as an incoming call: a click outside does not close it.
 */
export interface DialogProps {
  title: ReactNode;
  onClose: () => void;
  onSubmit?: () => void;
  children?: ReactNode;
  /** Buttons at the bottom, right aligned. */
  actions?: ReactNode;
  /** Widest the card gets, in pixels; default 460. */
  width?: number;
  /** A line under the title, read out with it. */
  description?: ReactNode;
  alert?: boolean;
  /** Extra classes on the card. */
  className?: string;
  /** Extra classes on the backdrop and the viewport, for a dialog that must sit above the others (an incoming call). */
  overlayClassName?: string;
}

export function Dialog({ title, onClose, onSubmit, children, actions, width = 460, description, alert = false, className, overlayClassName }: DialogProps) {
  const Parts = alert ? AlertDialog : Base;
  const overlay = overlayClassName ? ` ${overlayClassName}` : "";
  return (
    <Parts.Root open onOpenChange={(open) => !open && onClose()}>
      <Parts.Portal>
        <Parts.Backdrop className={`dlg-backdrop${overlay}`} />
        <Parts.Viewport className={`dlg-viewport${overlay}`}>
          <Parts.Popup
            className={`card stack dlg${className ? ` ${className}` : ""}`}
            style={{ width: `min(${width}px, 100%)` }}
            render={onSubmit ? <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} /> : undefined}
          >
            <Parts.Title render={<h2 />}>{title}</Parts.Title>
            {description && <Parts.Description className="dlg-desc muted small">{description}</Parts.Description>}
            {children}
            {actions && <div className="row dlg-actions">{actions}</div>}
          </Parts.Popup>
        </Parts.Viewport>
      </Parts.Portal>
    </Parts.Root>
  );
}
